import React, { useState } from 'react';
import {
  Activity,
  Users,
  TrendingUp,
  Clock,
  Zap,
  ChevronRight,
  BarChart2,
  Filter,
  Layers,
  CheckCircle2
} from 'lucide-react';
import { ProductFunnelModel, CohortRetentionMatrix } from '../../types';
import { analyticsEngine } from '../../services/analyticsEngine';

export const FunnelsCohortsView: React.FC = () => {
  const [funnels] = useState<ProductFunnelModel[]>(analyticsEngine.getFunnels());
  const [retentionMatrix] = useState<CohortRetentionMatrix[]>(analyticsEngine.getRetentionMatrix());
  const [selectedFunnel, setSelectedFunnel] = useState<ProductFunnelModel>(funnels[0]);

  return (
    <div className="space-y-8 pb-12">
      {/* Hero Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold border border-blue-500/30">
          <Activity className="w-3.5 h-3.5" />
          YamooPay Product Conversion Funnels & Cohort Retention Engine
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Product Conversion & Retention Intelligence</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Measure user journey progression from initial organic entrance to first useful financial calculation result, scenario saving, and recurring monthly retention.
        </p>
      </div>

      {/* Primary Funnel Selector & Visualizer */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">
              Funnel Drop-Off Intelligence
            </span>
            <h2 className="text-xl font-bold text-slate-900 mt-1">
              {selectedFunnel.funnelName}
            </h2>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {funnels.map((fn) => (
              <button
                key={fn.id}
                onClick={() => setSelectedFunnel(fn)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  selectedFunnel.id === fn.id
                    ? 'bg-blue-600 text-white shadow'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                {fn.toolDomain}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-600">{selectedFunnel.description}</p>

        {/* Funnel Visual Step Cards */}
        <div className="space-y-3 pt-2">
          {selectedFunnel.steps.map((step, index) => {
            const isFirst = index === 0;
            return (
              <div key={step.stepNumber} className="relative p-4 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center font-mono">
                      {step.stepNumber}
                    </span>
                    <div>
                      <h3 className="font-bold text-sm text-slate-900">{step.name}</h3>
                      <span className="text-[10px] font-mono text-slate-400">{step.eventName}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-xs font-mono">
                    <div>
                      <span className="text-slate-400">Users:</span> <strong className="text-slate-900 font-bold">{step.usersCount.toLocaleString()}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400">Step Conv:</span> <strong className="text-emerald-700 font-bold">{step.conversionRatePct}%</strong>
                    </div>
                    {!isFirst && (
                      <div>
                        <span className="text-slate-400">Drop-off:</span> <strong className="text-red-600 font-bold">{step.dropOffRatePct}%</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress Bar Visualizer */}
                <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${step.conversionRatePct}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cohort Retention Matrix */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
        <div className="border-b border-slate-100 pb-4">
          <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Meaningful Return Tool Sessions
          </div>
          <h2 className="text-xl font-bold text-slate-900 mt-1">
            Cohort Retention Matrix (Meaningful Calculations)
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Measures returning users who execute at least one valid financial calculation within D1, D7, D30, W1, W4, and M3.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-bold uppercase text-[10px]">
                <th className="py-2.5 px-3">Acquisition Cohort</th>
                <th className="py-2.5 px-3">Cohort Size</th>
                <th className="py-2.5 px-3">Day 1</th>
                <th className="py-2.5 px-3">Day 7</th>
                <th className="py-2.5 px-3">Day 30</th>
                <th className="py-2.5 px-3">Week 1</th>
                <th className="py-2.5 px-3">Week 4</th>
                <th className="py-2.5 px-3">Month 3</th>
                <th className="py-2.5 px-3">Total Calculations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-mono text-slate-700">
              {retentionMatrix.map((cohort) => (
                <tr key={cohort.cohortName} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-2.5 px-3 font-bold text-slate-900">{cohort.cohortName}</td>
                  <td className="py-2.5 px-3 font-bold">{cohort.size.toLocaleString()}</td>
                  <td className="py-2.5 px-3 font-bold text-blue-700 bg-blue-50/60">{cohort.d1Pct}%</td>
                  <td className="py-2.5 px-3 font-bold text-blue-700 bg-blue-50/80">{cohort.d7Pct}%</td>
                  <td className="py-2.5 px-3 font-extrabold text-emerald-700 bg-emerald-50">{cohort.d30Pct}%</td>
                  <td className="py-2.5 px-3">{cohort.w1Pct}%</td>
                  <td className="py-2.5 px-3">{cohort.w4Pct}%</td>
                  <td className="py-2.5 px-3 text-slate-500">{cohort.m3Pct}%</td>
                  <td className="py-2.5 px-3 font-extrabold text-slate-900">{cohort.meaningfulCalculationsCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
